import clsx from 'clsx'
import map from 'lodash/map'
import sortBy from 'lodash/sortBy'
import React, { FC, useMemo, useState } from 'react'

import SvgPlus from '@Assets/SVG/Plus'
import { BuyButtonV2 } from '@Components/BuyButton/v2'
import { ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import { Slider, useSlider } from '@Components/Slider'
import { SliderIndicator } from '@Components/Slider/SliderIndicator/SliderIndicator'
import { CheckboxIndicator } from '@CoreComponents/Checkboxes/checkboxIndicator'
import { Price } from '@CoreComponents/Prices/Price'
import { Product, TacticBuildYourDiscount } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'
import { createAddToCartTacticFromOffer } from '@Shared/Utilities/AddToCartHelper'

import styles from './BuildYourDiscountTactic.module.scss'

interface LocalProduct {
  discountPrice?: string
  id: string
  imageUrl?: string
  price?: string
  title?: string
  url?: string
}

const ProductTile: FC<{
  checked?: boolean
  className?: string
  disabled?: boolean
  enableLink?: boolean
  onClick?: () => void
  product: LocalProduct
}> = ({ checked, className, disabled, enableLink = false, onClick, product }) => {
  if (!product.imageUrl) {
    return null
  }

  let title = (
    <p className={styles.title}>
      {product.title}
    </p>
  )

  if (enableLink && product?.url) {
    title = (
      <Link className={styles.title} href={product.url} onClick={e => e.stopPropagation()}>
        {product.title}
      </Link>
    )
  }

  return (
    <button className={className} disabled={disabled} onClick={onClick} type="button">
      <div className={styles.imageWrapper}>
        <ImageV4 height={90} src={product.imageUrl} width={90} />
        {!disabled && (
          <CheckboxIndicator
            checked={checked}
            className={styles.checkboxIndicator}
          />
        )}
      </div>
      <div className={styles.productInfo}>
        {title}
        <Price className={styles.price} discountPrice={product.discountPrice} price={product.price || ''} theme="white" />
      </div>
    </button>
  )
}

function areArraysEqual(arr1: string[], arr2: string[]): boolean {
  if (arr1.length !== arr2.length) { return false }
  const sortedArr1 = [...arr1].sort()
  const sortedArr2 = [...arr2].sort()
  return sortedArr1.every((value, index) => value === sortedArr2[index])
}
interface BuildYourDiscountTacticProps {
  disable?: boolean
  primaryProduct: Product
  tactic?: TacticBuildYourDiscount
}
export const BuildYourDiscountTactic: FC<BuildYourDiscountTacticProps> = ({ disable, primaryProduct, tactic }) => {
  const { t } = useTranslation()
  const { onSlide, sliderState } = useSlider()
  const [selectedBundleMatchingProductsOffer, setSelectedBundleMatchingProductsOffer] = useState<string[]>(
    [primaryProduct.id, ...(tactic?.products?.map(p => p.id) || [])],
  )
  const bundleMatchingProductsOffer = useMemo(() => tactic?.items?.reduce((acc, item) => {
    if (acc) {
      return acc
    }
    const itemOfferIDs: string[] = item.offers?.map(itemOffer => itemOffer.productID) || []
    if (areArraysEqual(itemOfferIDs, selectedBundleMatchingProductsOffer)) {
      return item
    }
    return acc
  }, undefined), [selectedBundleMatchingProductsOffer, tactic?.items])

  const primaryProductTileData = useMemo(() => ({
    id: primaryProduct.id,
    imageUrl: (primaryProduct as any)?.primaryAsset?.url,
    title: primaryProduct.name || '',
    price: bundleMatchingProductsOffer?.offers?.find(offer => offer.productID === primaryProduct.id)?.offer.price?.format || undefined,
    discountPrice: bundleMatchingProductsOffer?.offers?.find(offer => offer.productID === primaryProduct.id)?.offer.price?.discount?.format || undefined,
    url: primaryProduct.url || '',
  }), [bundleMatchingProductsOffer?.offers, primaryProduct])
  const products = useMemo(() => (tactic?.products || []).map(p => ({
    id: p.id || '',
    imageUrl: (p as any)?.primaryAsset?.url || '',
    title: p.name || '',
    price: bundleMatchingProductsOffer?.offers?.find(offer => offer.productID === p.id)?.offer.price?.format || undefined,
    discountPrice: bundleMatchingProductsOffer?.offers?.find(offer => offer.productID === p.id)?.offer.price?.discount?.format || undefined,
    url: p.url || '',
  })), [bundleMatchingProductsOffer?.offers, tactic?.products])
  const [selectedIDs, setSelectedIDs] = useState(products.reduce((a, p) => ({ ...a, [p.id]: true }), {}))
  if (!tactic || (tactic.__typename !== 'TacticBuildYourDiscount')) {
    return null
  }
  const discountPrice = bundleMatchingProductsOffer?.price.discount?.format || undefined
  const price = bundleMatchingProductsOffer?.price.format || undefined

  return (
    <div className={styles.buildYourDiscount}>
      <div className={styles.bundleSource}>
        <div className={styles.buildYourDiscountHeader}>
          <p className={styles.header}>{t('common:COMBINE_WITH_LABEL')}</p>
          <div className={clsx(styles.prices, 'bundle-price')}>
            {(discountPrice || price) && (
              <p className={clsx(styles.buyTogether, 'buy-as-a-bundle')}>
                {t('products:BUNDLE_DISCOUNT_LABEL')}
              </p>
            )}
            <Price className={styles.price} discountPrice={discountPrice} price={price || ''} theme="white" />
          </div>
          <BuyButtonV2
            className={clsx(styles.buyButton)}
            color="transparentWhite"
            disabled={disable || !bundleMatchingProductsOffer}
            innerSpacing="medium"
            productID={primaryProduct.id}
            products={sortBy((bundleMatchingProductsOffer?.offers || []), item => (item.productID === primaryProduct.id ? 0 : 1))
              .map(offeredProduct => ({
                groupIndex: 0,
                productID: offeredProduct.productID,
                quantity: 1,
                tactic: createAddToCartTacticFromOffer(offeredProduct.offer),
              }))}
            sourceID="build_your_discount"
          >
            <>
              {t('cms:ADD_TO_CART')}
              {bundleMatchingProductsOffer?.offers && bundleMatchingProductsOffer?.offers?.length > 0
                ? ` (${bundleMatchingProductsOffer?.offers?.length})`
                : null}
            </>
          </BuyButtonV2>
        </div>
        <ProductTile
          key={`BundleMatchingProducts_primary_${primaryProduct.id}`}
          checked
          className={clsx(styles.product, styles.primaryProduct)}
          disabled
          product={primaryProductTileData}
        />
      </div>
      <div className={styles.productsToBuild}>
        <SvgPlus className={styles.plus} />
        <div className={styles.sliderWrapper}>
          <Slider onSlide={onSlide}>
            {map(products, product => (
              <ProductTile
                key={`BundleMatchingProducts_${product.id}`}
                checked={selectedIDs[product.id]}
                className={styles.product}
                enableLink
                onClick={() => {
                  const ids = ({
                    ...selectedIDs,
                    [product.id]: !selectedIDs[product.id],
                  })
                  setSelectedIDs(ids)
                  setSelectedBundleMatchingProductsOffer([
                    primaryProduct.id, ...Object.keys(ids)
                      .filter(key => ids[key]),
                  ])
                }}
                product={product}
              />
            ))}
          </Slider>
          <SliderIndicator className={styles.sliderIndicator} sliderState={sliderState} />
        </div>
      </div>
    </div>
  )
}
